/**
 * Phase 19 — what the rules said about one hour / text / prompt group /
 * gateway: a badge per rule that fired (its reason on hover), one muted chip
 * for the rules that could not judge it; and, in a detail panel, the same as
 * a list with every rule's own words.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import type { DropGenRuleHit } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import { EXCLUDE_LOOK, RULE_ACTIONS } from './rule-surfaces';

const TONE: Record<string, string> = {
  exclude: EXCLUDE_LOOK.tone,
  block: 'bg-destructive/10 text-destructive',
  retire: 'bg-destructive/10 text-destructive',
  retire_group: 'bg-destructive/10 text-destructive',
  leave_pool: 'bg-destructive/10 text-destructive',
  no_extra: 'bg-warning/15 text-foreground',
  last_resort: 'bg-warning/15 text-foreground',
  proven: 'bg-success/15 text-foreground',
  extra_first: 'bg-success/15 text-foreground',
};

const PILL = 'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-semibold';

/** The action's icon and its short word — the same pair the rule card's picker shows. */
const ActionLabel: FC<{ action: string }> = ({ action }) => {
  const meta = RULE_ACTIONS[action];
  const Icon = meta?.icon;
  return (
    <>
      {Icon ? <Icon className="h-2.5 w-2.5 shrink-0" aria-hidden /> : null}
      {meta?.badge ?? action}
    </>
  );
};

export const RuleHitBadges: FC<{ hits: DropGenRuleHit[] | undefined; className?: string }> = ({ hits, className }) => {
  const fired = (hits ?? []).filter((h) => h.state === 'fired');
  const unknown = (hits ?? []).filter((h) => h.state === 'unknown');
  if (fired.length === 0 && unknown.length === 0) return null;
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-1', className)} data-testid="rule-hits">
      {fired.map((h) => (
        <span key={h.ruleId} className={cn(PILL, TONE[h.action] ?? 'bg-muted text-muted-foreground')} title={`${h.name}: ${h.reason}`}>
          <ActionLabel action={h.action} />
        </span>
      ))}
      {unknown.length > 0 ? (
        <span className={cn(PILL, 'border border-dashed border-border font-medium text-muted-foreground')} title={unknown.map((h) => `${h.name}: ${h.reason}`).join('\n')}>
          {unknown.length} rule{unknown.length > 1 ? 's' : ''} not judged
        </span>
      ) : null}
    </span>
  );
};

export const RuleHitList: FC<{ hits: DropGenRuleHit[] | undefined }> = ({ hits }) => {
  if (!hits || hits.length === 0) return null;
  return (
    <div className="flex flex-col gap-1" data-testid="rule-hit-list">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Rules</span>
      {hits.map((h) => (
        <p key={h.ruleId} className="text-xs">
          <span className={cn(PILL, 'mr-1.5', h.state === 'fired' ? TONE[h.action] ?? 'bg-muted' : 'border border-dashed border-border font-medium text-muted-foreground')}>
            {h.state === 'fired' ? <ActionLabel action={h.action} /> : 'not judged'}
          </span>
          <b>{h.name}</b> <span className="text-muted-foreground">— {h.reason}</span>
        </p>
      ))}
    </div>
  );
};

/** The Flow Optimizer's excluded mark (red square, red ban), with the word when `label` — the hour chart, card and table. */
export const ExcludedMark: FC<{ title?: string; label?: boolean; className?: string }> = ({ title, label, className }) => {
  const { Icon } = EXCLUDE_LOOK;
  if (!label)
    return (
      <span title={title} aria-label={title ?? 'excluded'} className={cn('inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm', EXCLUDE_LOOK.tone, className)}>
        <Icon className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden />
      </span>
    );
  return (
    <span title={title} className={cn(PILL, EXCLUDE_LOOK.tone, className)}>
      <Icon className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} aria-hidden /> excluded
    </span>
  );
};
